import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {t} from 'i18next';
import {useEffect, useRef, useState} from 'react';

import {loadDropboxEmbedder} from './dropboxEmbedder';
import styles from './ProductRoadmap.module.css';
import {RoadmapEmbedLoadingFrame} from './RoadmapEmbedLoadingFrame';

export const PaperRoadmapEmbed = ({
  appKey,
  url,
  title,
}: {
  appKey: string;
  url: string;
  title: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    let unmountEmbed: (() => void) | undefined;
    const container = containerRef.current;
    setStatus('loading');

    void loadDropboxEmbedder(appKey)
      .then((sdk) => {
        if (cancelled || !container) return;
        if (!sdk.isBrowserSupported()) throw new Error('Dropbox embedder is unsupported');
        const embed = sdk.embed({link: url}, container);
        unmountEmbed = () => sdk.unmount(embed);
        const iframe = container.querySelector('iframe');
        if (iframe) iframe.title = title;
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      unmountEmbed?.();
    };
  }, [appKey, url, title]);

  return (
    <>
      {status === 'error' && (
        <Box padding="24" role="status">
          <Text>{t('product_roadmap_paper_preview_error')}</Text>
        </Box>
      )}
      <Box className={styles.productRoadmapEmbedFrameShell} hidden={status === 'error'}>
        {status === 'loading' && <RoadmapEmbedLoadingFrame />}
        <div
          ref={containerRef}
          className={styles.productRoadmapEmbedFrame}
          data-testid="paper-roadmap-embed"
          aria-busy={status === 'loading'}
        />
      </Box>
    </>
  );
};
